import type { ReactNode } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import OrganizationProfile from './Pages/Organization/OrganizationProfile'
import UploadStaffData from './Pages/Staff/StaffBulkUpload'
import Shift from './Pages/Shift/Shift'
import Department from './Pages/Department/Department'
import LoginForm from './Pages/Login/LoginForm'
import Welcome from './Pages/Welcome/Welcome'
import User from './Pages/User/User'
import CheckInForm from './Pages/CheckIn/CheckInForm'
import CheckOutForm from './Pages/CheckOut/CheckOutForm'
import CheckInCheckOut from './Pages/CheckInCheckOut/CheckInCheckOut'
import ViewStaffDetail from './Pages/Staff/StaffDetail'
import AttendancePage from './Pages/Attendance/AttendancePage'
import StaffCreateUpdate from './Pages/Staff/StaffCreateUpdate'
import StaffList from './Pages/Staff/StaffList'
import StaffMerge from './Pages/Staff/StaffMerge'
import AttendanceEditorPage from './Pages/Attendance/AttendanceEditorPage'
import PrivacyPolicy from './Pages/PrivacyPolicy/PrivacyPolicy'
import Settings from './Pages/Settings/Settings'
import AuthRoute from './Pages/AuthRoute/AuthRoute'
import { WelcomeSidebarSlotProvider } from './common/layout/WelcomeSidebarSlotContext'
import WelcomeSidebarChrome from './Pages/Welcome/WelcomeSidebarChrome'
import LeaveManagement from './Pages/Leave/LeaveManagement'
import LeaveDetail from './Pages/Leave/LeaveDetail'
import PublicHolidays from './Pages/Leave/PublicHolidays'
import LeaveSettings from './Pages/Leave/LeaveSettings'
import ManageBalance from './Pages/Leave/ManageBalance'
import CarryForwardHistory from './Pages/Leave/CarryForwardHistory'
import LeaveCalendar from './Pages/Leave/LeaveCalendar'

/** Flat routes + optional sidebar chrome (no layout-route / Outlet nesting). */
function AuthedWelcomeChrome({ children }: { children: ReactNode }) {
	return (
		<AuthRoute>
			<WelcomeSidebarSlotProvider>
				<WelcomeSidebarChrome>{children}</WelcomeSidebarChrome>
			</WelcomeSidebarSlotProvider>
		</AuthRoute>
	)
}

const AppRoutes = () => {
	return (
		<Routes>
			{/* Public Routes */}
			<Route
				path='/login'
				element={
					<AuthRoute publicRoute>
						<LoginForm />
					</AuthRoute>
				}
			/>
			<Route
				path='/privacypolicy'
				element={
					<AuthRoute publicRoute>
						<PrivacyPolicy />
					</AuthRoute>
				}
			/>
			<Route
				path='/checkin'
				element={
					<AuthRoute>
						<CheckInForm />
					</AuthRoute>
				}
			/>
			<Route
				path='/checkout'
				element={
					<AuthRoute>
						<CheckOutForm />
					</AuthRoute>
				}
			/>

			{/* Protected — each path is independent; sidebar is injected by WelcomeSidebarChrome when applicable */}
			<Route
				path='/welcome'
				element={
					<AuthedWelcomeChrome>
						<Welcome />
					</AuthedWelcomeChrome>
				}
			/>
			<Route
				path='/admin-user'
				element={
					<AuthedWelcomeChrome>
						<User />
					</AuthedWelcomeChrome>
				}
			/>
			<Route
				path='/organization'
				element={
					<AuthedWelcomeChrome>
						<OrganizationProfile />
					</AuthedWelcomeChrome>
				}
			/>
			<Route
				path='/staff_bulk_upload'
				element={
					<AuthedWelcomeChrome>
						<UploadStaffData />
					</AuthedWelcomeChrome>
				}
			/>
			<Route
				path='/staff/merge'
				element={
					<AuthedWelcomeChrome>
						<StaffMerge />
					</AuthedWelcomeChrome>
				}
			/>
			<Route
				path='/staff/list'
				element={
					<AuthedWelcomeChrome>
						<StaffList />
					</AuthedWelcomeChrome>
				}
			/>
			<Route
				path='/staff/create'
				element={
					<AuthedWelcomeChrome>
						<StaffCreateUpdate />
					</AuthedWelcomeChrome>
				}
			/>
			<Route
				path='/staff/detail/:id'
				element={
					<AuthedWelcomeChrome>
						<ViewStaffDetail />
					</AuthedWelcomeChrome>
				}
			/>
			<Route
				path='/staff/:id'
				element={
					<AuthedWelcomeChrome>
						<StaffCreateUpdate />
					</AuthedWelcomeChrome>
				}
			/>
			<Route
				path='/shift'
				element={
					<AuthedWelcomeChrome>
						<Shift />
					</AuthedWelcomeChrome>
				}
			/>
			<Route
				path='/department'
				element={
					<AuthedWelcomeChrome>
						<Department />
					</AuthedWelcomeChrome>
				}
			/>
			<Route path='/role' element={<Navigate to='/department' replace />} />
			<Route
				path='/attendance'
				element={
					<AuthedWelcomeChrome>
						<AttendancePage />
					</AuthedWelcomeChrome>
				}
			/>
			<Route
				path='/attendance/:id'
				element={
					<AuthedWelcomeChrome>
						<AttendanceEditorPage />
					</AuthedWelcomeChrome>
				}
			/>
			<Route
				path='/leaves/holidays'
				element={
					<AuthedWelcomeChrome>
						<PublicHolidays />
					</AuthedWelcomeChrome>
				}
			/>
			<Route
				path='/leaves/settings'
				element={
					<AuthedWelcomeChrome>
						<LeaveSettings />
					</AuthedWelcomeChrome>
				}
			/>
			<Route
				path='/leaves/balance'
				element={
					<AuthedWelcomeChrome>
						<ManageBalance />
					</AuthedWelcomeChrome>
				}
			/>
			<Route
				path='/leaves/carry-forward-history'
				element={
					<AuthedWelcomeChrome>
						<CarryForwardHistory />
					</AuthedWelcomeChrome>
				}
			/>
			<Route
				path='/leaves/calendar'
				element={
					<AuthedWelcomeChrome>
						<LeaveCalendar />
					</AuthedWelcomeChrome>
				}
			/>
			<Route
				path='/leaves/:id'
				element={
					<AuthedWelcomeChrome>
						<LeaveDetail />
					</AuthedWelcomeChrome>
				}
			/>
			<Route
				path='/leaves'
				element={
					<AuthedWelcomeChrome>
						<LeaveManagement />
					</AuthedWelcomeChrome>
				}
			/>
			<Route
				path='/checkin-checkout'
				element={
					<AuthedWelcomeChrome>
						<CheckInCheckOut />
					</AuthedWelcomeChrome>
				}
			/>
			<Route
				path='/PrivacyPolicy'
				element={
					<AuthedWelcomeChrome>
						<PrivacyPolicy />
					</AuthedWelcomeChrome>
				}
			/>
			<Route
				path='/settings'
				element={
					<AuthedWelcomeChrome>
						<Settings />
					</AuthedWelcomeChrome>
				}
			/>
		</Routes>
	)
}

export default AppRoutes
